'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';

import { useClickOutside, useDebounce } from '@/hooks';
import { useGlobalSearch } from '@/services/query/globalSearch.queries';
import type { EventResponse } from '@/types';
import { getEventType } from '@/utils';

import { ROUTES } from '@/constants';
import type { BrowseFilter } from '@/constants/search.constants';

const getInitialRecentSearches = (): EventResponse[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const saved = window.localStorage.getItem('recent_searches');

  if (!saved) {
    return [];
  }

  try {
    return JSON.parse(saved) as EventResponse[];
  } catch {
    return [];
  }
};

interface UseSearchDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  searchQuery: string;
  status?: string;
  page?: number;
}

export const useSearchDropdown = ({
  isOpen,
  onClose,
  anchorRef,
  searchQuery,
  status,
  page = 1,
}: UseSearchDropdownProps) => {
  const router = useRouter();

  const dropdownRef = useClickOutside<HTMLDivElement>(() => {
    if (anchorRef.current.contains(event.target as Node)) {
      return;
    }
    onClose();
  }, isOpen);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 620 });
  const [recentSearches, setRecentSearches] = useState<EventResponse[]>(getInitialRecentSearches);

  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  // Map status to keep_closed_markets
  const keep_closed_markets = status === 'active' ? 0 : status === 'resolved' ? 1 : undefined;

  const { data, isLoading, isFetching } = useGlobalSearch({
    q: debouncedSearchQuery,
    keep_closed_markets,
    page,
    event_status: status,
  });

  const searchResults = {
    events: data?.events || [],
    profiles: data?.profiles || [],
  };

  const [activeTab, setActiveTab] = useState<'market' | 'profile'>('market');

  const hasAnyResults = searchResults.events.length > 0 || searchResults.profiles.length > 0;

  const showResults = searchQuery.length > 0;

  useEffect(() => {
    const calculatePosition = () => {
      if (anchorRef?.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: Math.max(rect.width, 620),
        });
      }
    };

    if (isOpen && anchorRef?.current) {
      calculatePosition();
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);

      return () => {
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isOpen, anchorRef]);

  // useClickOutside handles the outside clicks

  const addToRecent = (event: EventResponse) => {
    setRecentSearches((prev) => {
      const filtered = prev.filter((item) => item.id !== event.id);
      const updated = [event, ...filtered].slice(0, 5);
      localStorage.setItem('recent_searches', JSON.stringify(updated));
      return updated;
    });
  };

  const removeFromRecent = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecentSearches((prev) => {
      const updated = prev.filter((item) => item.id !== id);
      localStorage.setItem('recent_searches', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearAllRecent = useCallback(() => {
    setRecentSearches([]);
    localStorage.removeItem('recent_searches');
  }, []);

  const handleResultClick = (event: EventResponse) => {
    addToRecent(event);
    const eventType = getEventType(event);

    if (eventType === 'SPORTS_CARD') {
      const sportSlug = (event.league || event.tags?.[0]?.slug || 'live').toLowerCase();
      router.push(ROUTES.GAME_DETAIL(sportSlug, event.id));
    } else {
      router.push(ROUTES.MARKET_DETAILS(event.slug));
    }
    onClose();
  };

  const handleProfileClick = (profile: { proxyWallet: string }) => {
    router.push(ROUTES.LEADERBOARD_PORTFOLIO(profile.proxyWallet));
    onClose();
  };

  const handleBrowseClick = (filter: BrowseFilter) => {
    if (filter.path) {
      router.push(filter.path);
      onClose();
    }
  };

  const handleSeeAllClick = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (status && status !== 'all') params.set('status', status);
    const qs = params.toString();
    router.push(qs ? `${ROUTES.SEARCH}?${qs}` : ROUTES.SEARCH);
    onClose();
  };

  return {
    state: {
      position,
      recentSearches,
      searchResults,
      isLoading: isLoading || (isFetching && debouncedSearchQuery !== searchQuery),
      hasAnyResults,
      showResults,
      activeTab,
      hasMore: data?.hasMore || false,
      totalResults: data?.totalResults || 0,
    },
    ref: {
      dropdownRef,
    },
    handlers: {
      setActiveTab,
      handleResultClick,
      handleProfileClick,
      handleBrowseClick,
      handleSeeAllClick,
      removeFromRecent,
      clearAllRecent,
    },
  };
};
